import api from '.'

/**
 * Class representing the API for admin organization operations.
 * Provides methods to retrieve and manage information related to organizations within an administrative context.
 */
class AdminOrganizationApi {
    /**
     * Fetches information about a specific organization using its unique identifier.
     *
     * @param {string} organization_id - The unique identifier of the organization.
     * @returns {Promise<Object>} A promise that resolves to the organization's information data.
     */
    info(organization_id) {
        const payload = {
            organization_id
        }

        return api
            .sentRequest('/api/organization/admin/info', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Fetches the list of organizations for the admin.
     *
     * @returns {Promise<Object>} A promise that resolves to the data of the response, or an error catch response in case of a failure.
     */
    list() {
        const payload = {}

        return api
            .sentRequest('/api/organization/admin/list', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Creates a new organization with the provided details.
     *
     * @param {string} owner_user_id - The ID of the user who owns the organization.
     * @param {string} org_name - The name of the organization.
     * @param {string} org_short_code - The short code for the organization.
     * @param {boolean} org_is_enabled - Indicates if the organization is enabled.
     * @param {string} org_contact_first_name - The first name of the organization's contact person.
     * @param {string} org_contact_last_name - The last name of the organization's contact person.
     * @param {string} org_contact_email - The email address of the organization's contact person.
     * @param {string} org_contact_phone_number - The phone number of the organization's contact person.
     * @param {string} org_company_name - The name of the company of the organization.
     * @param {string[]} org_enabled_countries - An array of country codes where the organization is enabled.
     * @param {string} organization_billing_profile_id - The billing profile ID for the organization.
     * @returns {Promise<Object>} A promise that resolves to the response data from the create organization API.
     */
    create(
        owner_user_id,
        org_name,
        org_short_code,
        org_is_enabled,
        org_contact_first_name,
        org_contact_last_name,
        org_contact_email,
        org_contact_phone_number,
        org_company_name,
        org_enabled_countries,
        organization_billing_profile_id
    ) {
        const payload = {
            owner_user_id,
            org_name,
            org_short_code,
            org_is_enabled,
            org_contact_first_name,
            org_contact_last_name,
            org_contact_email,
            org_contact_phone_number,
            org_company_name,
            org_enabled_countries,
            organization_billing_profile_id
        }

        return api
            .sentRequest('/api/organization/admin/create', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates the organization details with the provided parameters.
     *
     * @param {string} organization_id - The unique identifier of the organization.
     * @param {string} org_name - The name of the organization.
     * @param {string} org_short_code - The short code for the organization.
     * @param {boolean} org_is_enabled - Indicates if the organization is enabled.
     * @param {string} org_contact_first_name - The first name of the contact person.
     * @param {string} org_contact_last_name - The last name of the contact person.
     * @param {string} org_contact_email - The email address of the contact person.
     * @param {string} org_contact_phone_number - The phone number of the contact person.
     * @param {string} org_company_name - The name of the company.
     * @param {Array<string>} org_enabled_countries - List of countries where the organization is enabled.
     * @param {string} organization_billing_profile_id - The billing profile ID of the organization.
     * @param {boolean} org_audience_target_countries_enabled - Whether audience targeting by countries is enabled.
     * @param {boolean} org_geofence_countries_enabled - Whether geofencing by countries is enabled.
     * @returns {Promise<Object>} The response data from the API call.
     */
    update(
        organization_id,
        org_name,
        org_short_code,
        org_is_enabled,
        org_contact_first_name,
        org_contact_last_name,
        org_contact_email,
        org_contact_phone_number,
        org_company_name,
        org_enabled_countries,
        organization_billing_profile_id,
        org_audience_target_countries_enabled,
        org_geofence_countries_enabled
    ) {
        const payload = {
            organization_id,
            org_name,
            org_short_code,
            org_is_enabled,
            org_contact_first_name,
            org_contact_last_name,
            org_contact_email,
            org_contact_phone_number,
            org_company_name,
            org_enabled_countries,
            organization_billing_profile_id,
            org_audience_target_countries_enabled,
            org_geofence_countries_enabled
        }

        return api
            .sentRequest('/api/organization/admin/update', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const adminOrganizationApi = new AdminOrganizationApi()
